import {
  Box,
  Button,
  Checkbox,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { useTranslation } from 'react-i18next'

interface DocumentationContentPublishTabProps {
  draftVersionCodes: string[]
  nonDefaultPublishedVersionCodes: string[]
  publishVersionCode: string
  publishSetDefault: boolean
  defaultVersionCode: string
  publishPending: boolean
  setDefaultPending: boolean
  anyVersionPending: boolean
  onSetPublishVersionCode: (value: string) => void
  onSetPublishSetDefault: (value: boolean) => void
  onSetDefaultVersionCode: (value: string) => void
  onPublishFromTab: () => void
  onSetDefaultFromTab: () => void
}

export function DocumentationContentPublishTab({
  draftVersionCodes,
  nonDefaultPublishedVersionCodes,
  publishVersionCode,
  publishSetDefault,
  defaultVersionCode,
  publishPending,
  setDefaultPending,
  anyVersionPending,
  onSetPublishVersionCode,
  onSetPublishSetDefault,
  onSetDefaultVersionCode,
  onPublishFromTab,
  onSetDefaultFromTab,
}: DocumentationContentPublishTabProps) {
  const { t } = useTranslation()

  return (
    <VStack align="stretch" spacing={4}>
      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={3}>
          {t('documentationContent.publish.publishDraftTitle')}
        </Text>
        <HStack spacing={3} flexWrap="wrap" align="center">
          <Box w={{ base: 'full', sm: '280px' }}>
            <DropdownSelect
              size="sm"
              value={publishVersionCode}
              options={draftVersionCodes.map((code) => ({
                value: code,
                label: code,
              }))}
              placeholder={t('documentationContent.publish.selectDraftVersion')}
              onChange={(value) => onSetPublishVersionCode(value)}
            />
          </Box>
          <Checkbox
            isChecked={publishSetDefault}
            onChange={(event) => onSetPublishSetDefault(event.target.checked)}
          >
            {t('documentationContent.publish.setAsDefaultAfterPublish')}
          </Checkbox>
          <Button
            colorScheme="blue"
            onClick={onPublishFromTab}
            isLoading={publishPending}
            isDisabled={!publishVersionCode || anyVersionPending}
          >
            {t('documentationContent.publish.publishSelectedDraft')}
          </Button>
        </HStack>
      </Box>

      <Box borderWidth="1px" borderRadius="md" p={3}>
        <Text fontWeight="semibold" mb={3}>
          {t('documentationContent.publish.switchDefaultTitle')}
        </Text>
        <HStack spacing={3} flexWrap="wrap">
          <Box w={{ base: 'full', sm: '280px' }}>
            <DropdownSelect
              size="sm"
              value={defaultVersionCode}
              options={nonDefaultPublishedVersionCodes.map((code) => ({
                value: code,
                label: code,
              }))}
              placeholder={t('documentationContent.publish.selectPublishedVersion')}
              onChange={(value) => onSetDefaultVersionCode(value)}
            />
          </Box>
          <Button
            colorScheme="teal"
            onClick={onSetDefaultFromTab}
            isLoading={setDefaultPending}
            isDisabled={!defaultVersionCode || anyVersionPending}
          >
            {t('documentationContent.publish.setDefault')}
          </Button>
        </HStack>
      </Box>
    </VStack>
  )
}
